import * as React from "react";
import { CheckCircle2, Copy, Globe, Loader2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAddDomainToPlan } from "@/hooks/useEnrollments";
import { ApiError } from "@/lib/api";
import type { AddPlanDomainResponse } from "@/types/api";

interface AddPlanDomainDialogProps {
  subscriptionId: number;
  planName: string;
  /** Free domain slots remaining on this plan. Negative = unlimited. */
  slotsLeft: number;
}

/**
 * "Add domain" on a plan card. Connects a domain the customer ALREADY OWNS
 * (free — they just point nameservers at us). Registering or transferring a
 * domain is a paid step and stays in the purchase flow.
 */
export function AddPlanDomainDialog({ subscriptionId, planName, slotsLeft }: AddPlanDomainDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [domain, setDomain] = React.useState("");
  const [result, setResult] = React.useState<AddPlanDomainResponse | null>(null);

  const { toast } = useToast();
  const add = useAddDomainToPlan(subscriptionId);

  const unlimited = slotsLeft < 0;

  function reset() {
    setDomain("");
    setResult(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const res = await add.mutateAsync(domain.trim().toLowerCase());
      setResult(res);
    } catch (err) {
      if (err instanceof ApiError) {
        const title =
          err.code === "domain_taken"          ? "Domain already in HosterDash"
          : err.code === "plan_domain_capacity" ? "Plan is full"
          : err.code === "invalid_domain"       ? "Invalid domain"
          : "Couldn't add domain";
        toast({ title, description: err.message, variant: "destructive" });
      } else {
        toast({
          title: "Couldn't add domain",
          description: err instanceof Error ? err.message : "Try again.",
          variant: "destructive"
        });
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus className="h-4 w-4" />
          Add domain{!unlimited && slotsLeft > 0 && (
            <span className="text-muted-foreground">· {slotsLeft} left</span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent>
        {result ? (
          <SuccessView result={result} onDone={() => { setOpen(false); reset(); }} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Add a domain to {planName}</DialogTitle>
              <DialogDescription>
                Connect a domain you already own — free. We'll set up hosting and
                email for it; you just point its nameservers at us. Want a brand-new
                domain instead? Buy it from the Plans page.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="planDomain">Domain name</Label>
                <Input
                  id="planDomain"
                  required
                  autoFocus
                  value={domain}
                  onChange={e => setDomain(e.target.value)}
                  placeholder="example.com"
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="text-xs text-muted-foreground">
                  The domain stays registered where it is — renewals remain with your
                  current registrar.
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={add.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={add.isPending || !domain.trim()}>
                  {add.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add domain
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuccessView({ result, onDone }: { result: AddPlanDomainResponse; onDone: () => void }) {
  const { toast } = useToast();

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: "Copied", variant: "success" });
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-brand-green" />
          {result.container.domain_name} added
        </DialogTitle>
        <DialogDescription>
          {result.hosting_provisioned
            ? "Hosting is set up. One last step to go live:"
            : "Hosting is being set up automatically (check the domain page in a few minutes). One last step to go live:"}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Set these nameservers at your current registrar
        </p>
        {result.nameservers.map(ns => (
          <div key={ns} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 font-mono text-foreground">
              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
              {ns}
            </span>
            <button
              type="button"
              onClick={() => copy(ns)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={`Copy ${ns}`}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <p className="pt-1 text-xs text-muted-foreground">
          Nameserver changes can take up to 24–48 hours to propagate. Email and
          hosting activate automatically once they point at us.
        </p>
      </div>

      <DialogFooter>
        <Button onClick={onDone}>Done</Button>
      </DialogFooter>
    </>
  );
}
